import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalContainerComponent } from 'src/app/shared/modal-container/modal-container.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NgbDateParserFormatter,
  NgbDatepickerModule,
} from '@ng-bootstrap/ng-bootstrap';
import { CustomDateParserFormatter } from 'src/app/core/providers/date/CustomDateParserFormatter';
import {
  ChangeAssigneeTaskQueryParams,
  PlanningTask,
  TaskFormPlanningDto,
  UpdateTaskQueryParams,
  UpdateTaskResponse,
  UpdateTaskTimeQueryParams,
} from 'src/app/core/providers/planning/planning.models';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from 'src/app/core/providers/api/api.service';
import {
  datePickerToDateString,
  formatDateStringToDatePicker,
} from 'src/app/core/helpers/date.helpers';
import { planningDateRange, planningLocalDay } from 'src/app/core/helpers/planning-time.helpers';
import { getYearsRange } from 'src/app/core/helpers/date.helpers';
import { TaskTimeDisplayService } from 'src/app/core/providers/task-time-display/task-time-display.service';

@Component({
  selector: 'app-task-edit-modal',
  standalone: true,
  imports: [
    ModalContainerComponent,
    ReactiveFormsModule,
    NgbDatepickerModule,
    CommonModule,
    SharedModule,
    TranslateModule,
  ],
  templateUrl: './task-edit-modal.component.html',
  styleUrl: './task-edit-modal.component.scss',
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
})
export class TaskEditModalComponent implements OnInit {
  @Input() task!: TaskFormPlanningDto;
  @Input() planningTask: PlanningTask | null = null;
  @Input() isMasterCard: boolean = false; // Indicateur pour les cartes maîtresses
  @Output() refreshWeeklyPlanning = new EventEmitter<any>();
  
  editForm: FormGroup;
  submitted: boolean = false;
  isSaving = false;
  listYear: number[] = [];
  millesimeDayAndMonth: string = '31/12';

  constructor(
    public activeModal: NgbActiveModal,
    public translate: TranslateService,
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private taskTimeDisplay: TaskTimeDisplayService
  ) {
    this.editForm = this.formBuilder.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(150),
        ],
      ],
      millesime: ['', [Validators.required]],
      dueDate: ['', [Validators.required]],
      plannedDate: ['', [Validators.required]],
      initialDuration: [this.taskTimeDisplay.formatInput(0), [Validators.required]],
      executor: [null, [Validators.required]],
      priority: ['0'],
      category: [null],
      description: [''],
      billingType: [''],
      amount: [0],
      amountFiscal: [null as number | null],
      fiscalPaymentMode: [null as string | null],
    });
  }

  ngOnInit(): void {
    this.listYear = getYearsRange(5);
    this.initForm();
  }

  initForm(): void {
    // Préparer le millésime pour le formulaire (extraire la date seulement si elle existe)
    let initialMillesime = null;
    const editablePlannedDate = this.getEditablePlannedDate();
    if (this.task.millesime) {
      initialMillesime = this.task.millesime.split('T')[0];
    }
    
    this.editForm.patchValue({
      name: this.task.name,
      dueDate: this.task.dueDate
        ? formatDateStringToDatePicker(this.task.dueDate)
        : null,
      plannedDate: editablePlannedDate
        ? formatDateStringToDatePicker(editablePlannedDate)
        : null,
      initialDuration: this.getEditableDuration()
        ? this.taskTimeDisplay.formatInput(this.getEditableDuration())
        : this.taskTimeDisplay.formatInput(0),
      executor: this.getEditableExecutor(),
      priority: this.task.priority || '0',
      category: this.getCategory(this.task),
      description: this.task.description,
      billingType: this.task.billingType || '',
      amount: this.task.amount || 0,
      amountFiscal:
        this.task.amountFiscal !== undefined && this.task.amountFiscal !== null
          ? this.task.amountFiscal
          : null,
      fiscalPaymentMode: this.getFiscalPaymentModeIri(this.task),
      millesime: initialMillesime,
    });

    // Spécialisation selon le type de tâche
    if (this.task.type === 'WORKFLOW' || this.task.type === 'OBLIGATION') {
      // Pour les tâches maîtresses, seuls certains champs sont désactivés
      if (this.isMasterTask()) {
        // Tâche maîtresse : désactiver seulement le nom
        this.editForm.get('name')?.disable();
      } else {
        // Tâche WORKFLOW/OBLIGATION normale : désactiver name, millesime, category
        this.editForm.get('name')?.disable();
        this.editForm.get('millesime')?.disable();
        this.editForm.get('category')?.disable();
      }
    }

    // Charger les informations de la société pour déterminer le jour/mois du millésime
    const companyId = (this.task.company as any)?.id;
    if (companyId) {
      this.apiService.getById('companies', companyId).subscribe((company: any) => {
        let dayAndMonth = '31/12';
        let year = '';
        let millesimeDate = '';
        if (company?.closingDate) {
          const date = new Date(company.closingDate);
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          year = date.getFullYear().toString();
          dayAndMonth = `${day}/${month}`;
        }
        this.millesimeDayAndMonth = dayAndMonth;
        // Vérifier le millésime actuel dans le formulaire
        const currentMillesime = this.editForm.get('millesime')?.value;
        
        // Si le formulaire n'a pas encore de millésime, le pré-remplir
        if (!currentMillesime) {
          const existingMillesime = this.task.millesime;
          if (existingMillesime) {
            // Extraire seulement la date (YYYY-MM-DD) du format ISO
            const dateOnly = existingMillesime.split('T')[0];
            this.editForm.patchValue({ millesime: dateOnly });
          } else {
            const currentYear = new Date().getFullYear().toString();
            const [d, m] = this.millesimeDayAndMonth.split('/');
            millesimeDate = `${currentYear}-${m}-${d}`;
            this.editForm.patchValue({ millesime: millesimeDate });
          }
        }
      });
    }
  }

  getCategory(task: TaskFormPlanningDto): string | null {
    return task.category && task.category['@id']
      ? task.category['@id']
      : task.category;
  }

  getTaskExecutor(): string | null {
    const executor = this.task.actors?.find(
      (actor: any) => actor.role?.code === 'EXECUTOR'
    );
    return executor?.user?.['@id'] || null;
  }

  getEditableExecutor(): string | null {
    if (this.isEditingTaskTime() && this.planningTask?.user?.['@id']) {
      return this.planningTask.user['@id'];
    }
    return this.getTaskExecutor();
  }

  getEditableDuration(): number {
    if (this.isEditingTaskTime() && typeof this.planningTask?.duration === 'number') {
      return this.planningTask.duration;
    }
    return this.task.initialDuration;
  }

  getEditablePlannedDate(): string | null {
    if (this.planningTask?.startTime) {
      return planningLocalDay(this.planningTask.startTime);
    }
    return this.task.plannedDate;
  }

  private buildUtcDateRange(date: string, durationInMinutes: number): {
    startTime: string;
    endTime: string;
  } {
    return planningDateRange(date, durationInMinutes, this.planningTask?.startTime);
  }

  isEditingTaskTime(): boolean {
    return !!this.planningTask && (
      this.task.status?.code === 'DONE' ||
      this.planningTask.timeType === 'ACTUAL' ||
      this.planningTask.timeType === 'PARTIAL'
    );
  }

  getFiscalPaymentModeIri(task: TaskFormPlanningDto): string | null {
    const m = task.fiscalPaymentMode as
      | { '@id'?: string }
      | string
      | null
      | undefined;
    if (m == null || m === '') {
      return null;
    }
    if (typeof m === 'string') {
      return m;
    }
    if (typeof m === 'object' && m['@id']) {
      return m['@id'];
    }
    return null;
  }

  /** Forme attendue par le DTO / l’API après fusion locale (évite une IRI « nue » issue du formulaire). */
  private normalizeFiscalPaymentModeForTask(
    formValue: string | null,
    response: UpdateTaskResponse
  ): TaskFormPlanningDto['fiscalPaymentMode'] {
    const fromResponse = response.fiscalPaymentMode;
    if (fromResponse && typeof fromResponse === 'object' && fromResponse['@id']) {
      return fromResponse as TaskFormPlanningDto['fiscalPaymentMode'];
    }
    if (typeof fromResponse === 'string' && fromResponse) {
      return { '@id': fromResponse, id: '', name: '', code: '' };
    }
    if (formValue) {
      return { '@id': formValue, id: '', name: '', code: '' };
    }
    return null;
  }

  private normalizeAmountFiscalForApi(
    raw: number | string | null | undefined
  ): number | null {
    if (raw === '' || raw === null || raw === undefined) {
      return null;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  showAmountField(): boolean {
    const type = this.editForm.get('billingType')?.value;
    return type === 'BILLABLE' || type === 'PAID';
  }

  showFiscalFields(): boolean {
    return true;
  }

  getDateLabel(): string {
    return this.task?.status?.code === 'DONE'
      ? 'Date de réalisation'
      : 'Date planifiée';
  }

  // Méthodes pour vérifier si les champs doivent être désactivés
  isFieldDisabled(fieldName: string): boolean {
    // Logique spéciale pour les tâches maîtresses
    if (this.isMasterTask()) {
      // Pour les tâches maîtresses, seuls certains champs sont modifiables
      const editableFields = ['millesime', 'dueDate', 'priority', 'category', 'description', 'billingType', 'amount', 'amountFiscal', 'fiscalPaymentMode'];
      return !editableFields.includes(fieldName);
    }
    
    // Logique normale pour WORKFLOW et OBLIGATION
    if (this.task.type === 'WORKFLOW' || this.task.type === 'OBLIGATION') {
      return ['name', 'millesime', 'category'].includes(fieldName);
    }
    return false;
  }

  isFieldReadonly(fieldName: string): boolean {
    // Logique spéciale pour les tâches maîtresses
    if (this.isMasterTask()) {
      // Pour les tâches maîtresses, seuls certains champs sont modifiables
      const editableFields = ['millesime', 'dueDate', 'priority', 'category', 'description', 'billingType', 'amount', 'amountFiscal', 'fiscalPaymentMode'];
      return !editableFields.includes(fieldName);
    }
    
    // Logique normale pour WORKFLOW et OBLIGATION
    if (this.task.type === 'WORKFLOW' || this.task.type === 'OBLIGATION') {
      return ['name', 'millesime', 'category'].includes(fieldName);
    }
    return false;
  }

  // Vérifier si c'est une tâche maîtresse
  isMasterTask(): boolean {
    // Si isMasterCard est passé explicitement, l'utiliser (priorité)
    if (this.isMasterCard) {
      return true;
    }
    
    // Sinon, vérifier la propriété isMaster de la tâche
    // Une tâche maîtresse peut être de type WORKFLOW ou OBLIGATION
    return (this.task.type === 'WORKFLOW' || this.task.type === 'OBLIGATION') && this.task.isMaster === true;
  }

  // Méthode pour vérifier si on est sur l'écran "suivi d'échéances"
  isMonitoringDeadline(): boolean {
    return window.location.pathname.includes('/monitoring-deadlines');
  }

  // Méthode pour vérifier si le champ échéance doit être limité au jour seulement
  isDueDateDayOnly(): boolean {
    // Sur l'écran "suivi d'échéances", pour les tâches maîtresses, permettre seulement la modification du jour
    if (this.isMonitoringDeadline() && this.isMasterTask()) {
      return true;
    }
    
    // Sur l'écran "suivi d'échéances", pour les autres tâches, permettre la modification du jour ET du mois
    if (this.isMonitoringDeadline()) {
      return false;
    }
    
    // Pour les tâches maîtresses sur les autres écrans, l'échéance est entièrement modifiable
    if (this.isMasterTask()) {
      return false;
    }
    
    // Logique normale pour WORKFLOW et OBLIGATION
    return this.task.type === 'WORKFLOW' || this.task.type === 'OBLIGATION';
  }

  // Méthode pour vérifier si on doit afficher le champ jour et mois (sans année)
  isDueDateDayAndMonthOnly(): boolean {
    // Uniquement sur l'écran "suivi d'échéances" pour WORKFLOW et OBLIGATION (non maîtresses)
    if (!this.isMonitoringDeadline()) {
      return false;
    }
    
    // Pour les tâches maîtresses, utiliser le datepicker complet
    if (this.isMasterTask()) {
      return false;
    }
    
    // Pour WORKFLOW et OBLIGATION sur "suivi d'échéances", afficher jour + mois
    return this.task.type === 'WORKFLOW' || this.task.type === 'OBLIGATION';
  }

  // Méthode pour extraire le jour de la date d'échéance
  getDueDateDay(): number {
    // D'abord, essayer de lire depuis le formulaire (qui a été initialisé correctement)
    const formDueDate = this.editForm.get('dueDate')?.value;
    if (formDueDate && typeof formDueDate === 'object' && formDueDate.day) {
      return formDueDate.day;
    }
    
    // Sinon, parser directement depuis this.task.dueDate
    if (this.task && this.task.dueDate) {
      // Si c'est déjà un objet datepicker
      if (typeof this.task.dueDate === 'object' && (this.task.dueDate as any).day) {
        return (this.task.dueDate as any).day;
      }
      
      // Sinon, parser la chaîne ISO
      if (typeof this.task.dueDate === 'string') {
        const datePart = this.task.dueDate.split('T')[0];
        const parts = datePart.split('-');
        if (parts.length === 3) {
          const day = parseInt(parts[2], 10);
          if (!isNaN(day)) {
            return day;
          }
        }
      }
    }
    return 1;
  }

  // Méthode pour extraire le mois de la date d'échéance
  getDueDateMonth(): number {
    // D'abord, essayer de lire depuis le formulaire
    const formDueDate = this.editForm.get('dueDate')?.value;
    if (formDueDate && typeof formDueDate === 'object' && formDueDate.month) {
      return formDueDate.month;
    }
    
    // Sinon, parser directement depuis this.task.dueDate
    if (this.task && this.task.dueDate) {
      // Si c'est déjà un objet datepicker
      if (typeof this.task.dueDate === 'object' && (this.task.dueDate as any).month) {
        return (this.task.dueDate as any).month;
      }
      
      // Sinon, parser la chaîne ISO
      if (typeof this.task.dueDate === 'string') {
        const datePart = this.task.dueDate.split('T')[0];
        const parts = datePart.split('-');
        if (parts.length === 3) {
          const month = parseInt(parts[1], 10);
          if (!isNaN(month)) {
            return month;
          }
        }
      }
    }
    return 1;
  }

  // Méthode pour extraire l'année de la date d'échéance
  getDueDateYear(): number {
    // D'abord, essayer de lire depuis le formulaire
    const formDueDate = this.editForm.get('dueDate')?.value;
    if (formDueDate && typeof formDueDate === 'object' && formDueDate.year) {
      return formDueDate.year;
    }
    
    // Sinon, parser directement depuis this.task.dueDate
    if (this.task && this.task.dueDate) {
      // Si c'est déjà un objet datepicker
      if (typeof this.task.dueDate === 'object' && (this.task.dueDate as any).year) {
        return (this.task.dueDate as any).year;
      }
      
      // Sinon, parser la chaîne ISO
      if (typeof this.task.dueDate === 'string') {
        const datePart = this.task.dueDate.split('T')[0];
        const parts = datePart.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0], 10);
          if (!isNaN(year)) {
            return year;
          }
        }
      }
    }
    return new Date().getFullYear();
  }

  // Méthode pour mettre à jour seulement le jour de l'échéance
  updateDueDateDay(day: number): void {
    const month = this.getDueDateMonth();
    const year = this.getDueDateYear();
    // Utiliser directement datePickerToDateString pour éviter les problèmes de timezone
    const datePickerValue = { year, month, day };
    this.editForm.patchValue({
      dueDate: datePickerValue
    });
  }

  // Méthode pour mettre à jour le jour ET le mois de l'échéance (année fixe)
  updateDueDateDayAndMonth(day: number, month: number): void {
    const year = this.getDueDateYear();
    const datePickerValue = { year, month, day };
    this.editForm.patchValue({
      dueDate: datePickerValue
    });
  }

  // Méthode pour gérer le changement de jour dans le select
  onDayChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    if (target && target.value) {
      this.updateDueDateDay(+target.value);
    }
  }

  // Méthode pour gérer le changement de jour dans le select (jour + mois)
  onDayChangeForDayAndMonth(event: Event): void {
    const target = event.target as HTMLSelectElement;
    if (target && target.value) {
      const day = +target.value;
      const month = this.getDueDateMonth();
      this.updateDueDateDayAndMonth(day, month);
    }
  }

  // Méthode pour gérer le changement de mois dans le select (jour + mois)
  onMonthChangeForDayAndMonth(event: Event): void {
    const target = event.target as HTMLSelectElement;
    if (target && target.value) {
      const month = +target.value;
      const day = this.getDueDateDay();
      this.updateDueDateDayAndMonth(day, month);
    }
  }

  // Méthode pour obtenir la liste des mois avec leurs labels
  getMonthsList(): Array<{ value: number; label: string }> {
    const months = [
      { value: 1, label: 'Janvier' },
      { value: 2, label: 'Février' },
      { value: 3, label: 'Mars' },
      { value: 4, label: 'Avril' },
      { value: 5, label: 'Mai' },
      { value: 6, label: 'Juin' },
      { value: 7, label: 'Juillet' },
      { value: 8, label: 'Août' },
      { value: 9, label: 'Septembre' },
      { value: 10, label: 'Octobre' },
      { value: 11, label: 'Novembre' },
      { value: 12, label: 'Décembre' },
    ];
    return months;
  }


  updateTask(): void {
    this.submitted = true;
    
    if (this.editForm.valid) {
      this.isSaving = true;
      
      const formValue = this.editForm.getRawValue();
      const plannedDate = datePickerToDateString(formValue.plannedDate);
      const initialDuration = this.taskTimeDisplay.parseInput(formValue.initialDuration);
      const amountFiscalPayload = this.normalizeAmountFiscalForApi(
        formValue.amountFiscal
      );
      const canEditCategory = !this.isFieldDisabled('category');
      const queryParams: UpdateTaskQueryParams = {
        id: this.task.id,
        name: formValue.name, // getRawValue() récupère la valeur même si le champ est désactivé
        description: formValue.description,
        dueDate: datePickerToDateString(formValue.dueDate),
        plannedDate: this.buildUtcDateRange(plannedDate, initialDuration).startTime,
        initialDuration,
        billingType: formValue.billingType || null,
        amount: formValue.amount,
        amountFiscal: amountFiscalPayload,
        fiscalPaymentMode: formValue.fiscalPaymentMode || null,
        priority: parseInt(formValue.priority || '0', 10),
        millesime: formValue.millesime || null,
      };
      if (canEditCategory) {
        queryParams.category = formValue.category;
      }

      const currentExecutor = this.getEditableExecutor();
      const planningChanged =
        plannedDate !== (this.getEditablePlannedDate() || null) ||
        initialDuration !== this.getEditableDuration() ||
        (formValue.executor || null) !== currentExecutor;

      this.apiService.update('tasks', queryParams).subscribe({
         next: (response: UpdateTaskResponse) => {
           // Extraire la date du millésime de la réponse (enlever l'heure si présente)
           const millesimeValue = response.millesime ? response.millesime.split('T')[0] : formValue.millesime;
           const amountFiscalResolved =
             response.amountFiscal !== undefined
               ? response.amountFiscal
               : amountFiscalPayload;
           const fiscalPaymentModeResolved =
             this.normalizeFiscalPaymentModeForTask(
               formValue.fiscalPaymentMode || null,
               response
             );
           
           // Convertir la date du formulaire en chaîne ISO si nécessaire
           const updatedTaskData = { 
             ...this.task, 
             ...formValue, 
             millesime: millesimeValue,
             amountFiscal: amountFiscalResolved,
             fiscalPaymentMode: fiscalPaymentModeResolved,
             category: canEditCategory ? formValue.category : this.task.category,
             // S'assurer que la priorité est un nombre
             priority: parseInt(formValue.priority || '0', 10),
             plannedDate,
             initialDuration,
             // Préserver la propriété column si elle existe
             column: this.task.column || (this.task as any).column
           };
           if (formValue.dueDate && typeof formValue.dueDate === 'object') {
             // Convertir l'objet datepicker en chaîne ISO
             const dueDateString = datePickerToDateString(formValue.dueDate);
             updatedTaskData.dueDate = dueDateString;
           }
           
           // Mettre à jour aussi dans l'objet task pour que le formulaire soit correctement pré-rempli au prochain chargement
           this.task.millesime = millesimeValue;

           if (!planningChanged) {
             this.isSaving = false;
             this.refreshWeeklyPlanning.emit({
               action: 'updateTask',
               taskId: this.task.id,
               updatedTask: updatedTaskData,
             });
             this.closeModal();
             return;
           }

           if (this.isEditingTaskTime() && this.planningTask) {
             const utcRange = this.buildUtcDateRange(plannedDate, initialDuration);
             const updatedTaskTime: UpdateTaskTimeQueryParams = {
               id: this.planningTask.id,
               task: this.task['@id'],
               user: formValue.executor,
               role: this.planningTask.role['@id'],
               startTime: utcRange.startTime,
               endTime: utcRange.endTime,
               timeType: this.task.status.code === 'DONE' ? 'ACTUAL' : this.planningTask.timeType,
               duration: initialDuration,
               description: formValue.description || '',
               action: this.task.status.code === 'DONE' ? 'DONE' : null,
             };

             this.apiService.update('task_times', updatedTaskTime).subscribe({
               next: () => {
                 this.isSaving = false;
                 const computedDurationDelta = initialDuration - this.planningTask!.duration;
                 this.refreshWeeklyPlanning.emit({
                   action: 'updateTaskTime',
                   id: this.planningTask!.id,
                   taskId: this.task.id,
                   duration: initialDuration,
                   startTime: updatedTaskTime.startTime,
                   endTime: updatedTaskTime.endTime,
                   timeType: updatedTaskTime.timeType,
                   task: {
                     ...updatedTaskData,
                     plannedDate,
                     computedDuration: (this.task.computedDuration || 0) + computedDurationDelta,
                   },
                 });
                 this.closeModal();
               },
               error: () => {
                 this.isSaving = false;
               },
             });
             return;
           }

           const changeAssigneeParams: ChangeAssigneeTaskQueryParams = {
             user: formValue.executor,
             role: 'EXECUTOR',
             plannedDate: this.buildUtcDateRange(plannedDate, initialDuration).startTime,
             duration: initialDuration,
           };

           this.apiService
             .create(`tasks/${this.task.id}/change_assignee`, changeAssigneeParams)
             .subscribe({
               next: () => {
                 this.isSaving = false;

                 if (updatedTaskData.actors?.length) {
                   const executorIndex = updatedTaskData.actors.findIndex(
                     (actor: any) => actor.role?.code === 'EXECUTOR'
                   );
                   if (executorIndex !== -1) {
                     updatedTaskData.actors[executorIndex] = {
                       ...updatedTaskData.actors[executorIndex],
                       user:
                         updatedTaskData.actors[executorIndex].user?.['@id'] === formValue.executor
                           ? updatedTaskData.actors[executorIndex].user
                           : {
                               '@id': formValue.executor,
                               id: formValue.executor?.split('/').pop() || '',
                               firstName: '',
                               lastName: '',
                             },
                     };
                   }
                 }

                 this.refreshWeeklyPlanning.emit({
                   action: 'changeAssignee',
                   taskId: this.task.id,
                   updatedTask: updatedTaskData,
                 });
                 this.closeModal();
               },
               error: () => {
                 this.isSaving = false;
               },
             });
         },
        error: () => {
          this.isSaving = false;
        },
      });
    }
  }

  closeModal(): void {
    this.activeModal.close();
  }
}
